import { useCallback, useEffect, useState } from 'react';

/**
 * Rola um contêiner `overflow-x: auto` arrastando com o mouse (portal-cotacoes/at-16).
 *
 * Pedido do Victor, 29/10/2026, na matriz de comparação: com 6 fornecedores a tabela é mais
 * larga que a tela, e a barra de rolagem fica lá embaixo, depois de todas as peças. Clicar num
 * espaço vazio e arrastar para o lado é o gesto natural; a barra continua existindo.
 *
 * O arrasto NÃO rouba o clique: só vira rolagem depois de `LIMIAR_PX` de movimento, e nunca
 * começa sobre botão, link, campo ou rótulo (a célula da oferta é um botão, e selecioná-la é o
 * que importa na tela). Toque em celular já rola nativamente e fica fora daqui.
 */
const LIMIAR_PX = 6;
const INTERATIVOS = 'button, a, input, select, textarea, label, [role="button"]';

export function useArrastarParaRolar<T extends HTMLElement>() {
  // Ref de CALLBACK, não `useRef`: a tabela é montada depois (aba, carga), e um efeito com
  // `ref.current` lido uma vez no mount encontraria `null` e nunca ligaria os ouvintes.
  const [el, setEl] = useState<T | null>(null);
  const ref = useCallback((node: T | null) => setEl(node), []);

  useEffect(() => {
    if (!el) return;

    let inicioX = 0;
    let inicioScroll = 0;
    let arrastando = false;
    let moveu = false;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const interativo = (e.target as HTMLElement | null)?.closest<HTMLElement>(INTERATIVOS);
      // Botão desabilitado não recebe clique; arrastar a partir dele é só arrastar.
      if (interativo && !(interativo as HTMLButtonElement).disabled) return;
      if (el.scrollWidth <= el.clientWidth) return;
      // Sem isto o navegador começa a SELECIONAR TEXTO no mousedown, e pôr `user-select: none`
      // depois não desfaz a seleção já iniciada. Fora de campo e botão não há foco a perder.
      e.preventDefault();
      arrastando = true;
      moveu = false;
      inicioX = e.clientX;
      inicioScroll = el.scrollLeft;
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!arrastando) return;
      const delta = e.clientX - inicioX;
      if (!moveu && Math.abs(delta) < LIMIAR_PX) return;
      if (!moveu) {
        moveu = true;
        el.classList.add('arrastando');
      }
      e.preventDefault();
      el.scrollLeft = inicioScroll - delta;
    };

    const terminar = () => {
      if (!arrastando) return;
      arrastando = false;
      el.classList.remove('arrastando');
    };

    // Um clique que virou arrasto não pode terminar selecionando texto ou disparando o clique
    // do elemento onde o mouse soltou.
    const onClickCapture = (e: MouseEvent) => {
      if (!moveu) return;
      e.stopPropagation();
      e.preventDefault();
      moveu = false;
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('click', onClickCapture, true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', terminar);
    window.addEventListener('blur', terminar);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', terminar);
      window.removeEventListener('blur', terminar);
    };
  }, [el]);

  /** Rola um "passo" para o lado, para os botões de seta da tela. */
  const rolar = (sentido: -1 | 1) => {
    if (!el) return;
    el.scrollBy({ left: sentido * Math.max(240, el.clientWidth * 0.6), behavior: 'smooth' });
  };

  return { ref, rolar };
}
